import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiClose } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { AssetPicker } from '../AssetPicker'
import { AssetTypePicker } from '../asset-type-picker/AssetTypePicker'
import { useCreateComplianceRule, type ComplianceRule } from '../../hooks/useComplianceApi'

/**
 * "Dupliquer et adapter" (spec 2026-10-27-21 § 5): copies a rule into a
 * member of its family, scoped to buildings or sites (it governs the assets
 * under them before any type match) or to an asset type. The manager then
 * edits the copy's numbers on its row. One obligation, one ticket.
 */
export function RuleCloneDialog({
    org,
    rule,
    onClose,
}: {
    org: string
    rule: ComplianceRule
    onClose: () => void
}) {
    const { t } = useTranslation('compliance')
    const create = useCreateComplianceRule(org)
    const [mode, setMode] = useState<'assets' | 'type'>('assets')
    const [assets, setAssets] = useState<{ uid: string; name: string }[]>([])
    const [typeUid, setTypeUid] = useState('')
    const [error, setError] = useState<string | null>(null)

    const ready = mode === 'assets' ? assets.length > 0 : !!typeUid
    const submit = () => {
        if (!ready) return
        setError(null)
        create.mutate(
            mode === 'assets'
                ? { clone_of: rule.uid, scope_asset_uids: assets.map((a) => a.uid) }
                : { clone_of: rule.uid, asset_type_uid: typeUid },
            {
                onSuccess: onClose,
                onError: (e: unknown) => {
                    const code = (e as { code?: string })?.code
                    setError(
                        code === 'FAMILY_MEMBERS_LIMIT'
                            ? t('business_rules.clone.limit')
                            : code === 'family_member_exists'
                                ? t('business_rules.family_add_exists')
                                : t('business_rules.clone.error'),
                    )
                },
            },
        )
    }

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose() }}>
            <DialogContent data-testid={`rule-clone-dialog-${rule.uid}`}>
                <DialogHeader>
                    <DialogTitle>{t('business_rules.clone.title')}</DialogTitle>
                    <DialogDescription>{t('business_rules.clone.description')}</DialogDescription>
                </DialogHeader>
                <div className="flex gap-2">
                    <Button size="sm" variant={mode === 'assets' ? 'default' : 'outline'} onClick={() => setMode('assets')} data-testid="rule-clone-mode-assets">
                        {t('business_rules.clone.mode_assets')}
                    </Button>
                    <Button size="sm" variant={mode === 'type' ? 'default' : 'outline'} onClick={() => setMode('type')} data-testid="rule-clone-mode-type">
                        {t('business_rules.clone.mode_type')}
                    </Button>
                </div>
                {mode === 'assets' ? (
                    <div className="space-y-2">
                        <AssetPicker
                            org={org}
                            onChange={(uid, name) => {
                                if (uid && !assets.some((a) => a.uid === uid)) setAssets((as) => [...as, { uid, name }])
                            }}
                            testId="rule-clone-asset-picker"
                            placeholder={t('business_rules.clone.pick_asset')}
                            assetTypeCodes={['building', 'building_group']}
                        />
                        <div className="flex flex-wrap gap-1.5" data-testid="rule-clone-assets">
                            {assets.map((a) => (
                                <span key={a.uid} className="inline-flex items-center gap-1 rounded-full border bg-muted/50 px-2 py-0.5 text-xs">
                                    {a.name}
                                    <button type="button" onClick={() => setAssets((as) => as.filter((x) => x.uid !== a.uid))} aria-label={t('business_rules.clone.remove_asset')}>
                                        <AppIcon path={mdiClose} size="xs" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    </div>
                ) : (
                    <AssetTypePicker
                        org={org}
                        value={typeUid}
                        onChange={setTypeUid}
                        restrictToDescendantsOf={rule.asset_type_uid || undefined}
                        placeholder={t('business_rules.family_add_pick')}
                        triggerTestId="rule-clone-type-picker"
                    />
                )}
                {error && <p className="text-sm text-destructive" data-testid="rule-clone-error">{error}</p>}
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>{t('business_rules.cancel')}</Button>
                    <Button onClick={submit} disabled={!ready || create.isPending} data-testid="rule-clone-submit">
                        {t('business_rules.clone.submit')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
